import React, {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState
} from 'react';
import { GameProvider } from 'contexts/GameContext';
import { GlobalProvider } from 'contexts/GlobalContext';
import { GridModeProvider } from 'contexts/GridModeContext';
import { OrientationOverrideProvider } from 'contexts/OrientationOverrideContext';
import { ScaleContextProvider } from 'contexts/ScaleContext';
import { TableActiveProvider } from 'contexts/TableActiveContext';
import { TableHistoryProvider } from 'contexts/TableHistoryContext';
import { TableIdProvider } from 'contexts/TableIdContext';
import { TableOnScreenProvider } from 'contexts/TableOnScreenContext';
import { TexasChatContextProvider } from 'contexts/TexasChatContext';
import {
  GRID_REBIND_MAX_MS,
  PENDING_GRID_OPEN_MAX_MS,
  RESTORE_MAX_WAIT_MS,
  TABLE_TRANSITION_MS,
  ViewManagerContext
} from 'contexts/ViewManagerContext';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useIsMobile } from 'hooks/useIsMobile';
import { useViewportWidth } from 'hooks/useViewportWidth';
import { GameInner } from 'pages/Game';
import { removeTable } from 'reducers/tables';
import {
  cellFlexBasis,
  clampTablesInView,
  TABLE_GAP_PX,
  TABLE_MIN_WIDTH_PX,
  tablesThatFit
} from 'utils/tableViewWidth';

import MaintenanceMarquee from 'components/MaintenanceSchedule/MaintenanceMarquee';
import { useMouseDragScroll } from 'components/MultiTableGrid/useMouseDragScroll';
import { prefersReducedMotion } from 'components/RebindingTableView/tableSwitchTransition';
import { useActiveTables } from 'components/SaloonLobby/useActiveTables';
import TableHeader from 'components/TableHeader';
import GridTableShutdownBanner from 'components/TableShutdownBanner/GridTableShutdownBanner';

import {
  actionQueueHead,
  advanceActionQueue,
  clearGridVisibility,
  publishGridVisibility,
  visibleFraction
} from './gridScrollChannel';
import { gridCountWithRebalanceHold, useBoundedHold } from './useBoundedHold';
import { orderGridSlots, resolveGridSelection, useGridSlots } from './useGridSlots';

import './MultiTableGrid.scss';

// How long a post-login landing on /grid gets before the empty-grid guard
// below applies its normal "no tables → back to lobby" rule. state.tables is
// always empty on a fresh page load (never persisted — see the guard's own
// comment) and only repopulates once the socket reconnects and resubscribes,
// which doesn't happen instantly. Without this grace window, a guest who was
// in Multi-Table View when they logged in gets bounced straight back to the
// lobby the instant the post-login hard reload lands, before their tables
// have any chance to reappear.
const POST_LOGIN_GRACE_MS = 8000;

/**
 * EXPERIMENT (Jason, 2026-08-12) — tables FILL the row; this is the floor, not
 * the size.
 *
 * Was a fixed 490px width: every cell rendered at exactly 490 no matter the
 * viewport, so a 1440px screen showed two tables and ~460px of dead felt-less
 * background, and players read that as "I lost my 4th table".
 *
 * Now 390 (an iPhone-13 logical width) is the MINIMUM a table may be squeezed
 * to. It only decides how many tables share the row:
 *
 *   tablesAcross = max(1, floor((innerWidth + gap) / (TABLE_MIN_WIDTH + gap)))
 *
 * The cells themselves flex-grow to divide the row equally — two tables take
 * 50% each, three take a third each, four take a quarter — so the viewport is
 * always fully used and per-table size follows the table count. Because the
 * row's capacity is derived from this minimum, an equal share can never fall
 * below it, which is why the cells need no explicit min-width of their own.
 *
 * NOTE ON THE NAME. This was `tablesPerPage`, left over from the paginated
 * grid that EN-10486 replaced with one horizontally-scrolling row. There are
 * no pages any more: every open table is on the same row and `tablesAcross` is
 * how many of them share the viewport right now, clamped by the player's
 * count preference, what the window fits, and how many are actually open. The
 * old name cost a reviewer real time on #3721 (it reads as a page size, so
 * `shouldSkipGridPaint`'s window-capacity argument looked like a count), which
 * is why it is gone.
 */
// Single source of truth for the floor width and gap — shared with the table
// view's header (strip vs spread) and the narrow handoff. See
// utils/tableViewWidth for the derivation notes.
const TABLE_MIN_WIDTH = TABLE_MIN_WIDTH_PX;
const TABLE_GAP = TABLE_GAP_PX;

/**
 * Whether the empty/mobile guard should bounce off /grid back to the lobby.
 * Mobile always bounces (the grid is desktop-only). The empty-table case is
 * suppressed during inPostLoginGrace — state.tables is always empty on a
 * fresh page load and only repopulates once the socket resubscribes, so
 * without the grace window a guest who was in Multi-Table View when they
 * logged in would get bounced before their tables ever reappear.
 * Exported so this decision is unit-testable without mounting the full
 * provider tree the component needs.
 */
export const shouldReturnToLobbyFromGrid = (
  isMobile: boolean,
  activeTableCount: number,
  inPostLoginGrace: boolean
): boolean => isMobile || (activeTableCount === 0 && !inPostLoginGrace);

/**
 * Whether the viewport is too narrow to be worth showing a grid at all.
 *
 * The grid earns its name only when at least two tables sit side by side. With
 * room for one, it IS single-table view — just wearing grid chrome and a pager,
 * which is strictly worse than the real single-table UX (no swipe, no full-width
 * felt). Since `tablesAcross` is derived from TABLE_MIN_WIDTH, "fits fewer than
 * two" means the viewport is under 2 × TABLE_MIN_WIDTH + gap — 784px at a 390px
 * floor.
 *
 * Pointer type cannot answer this. `useIsMobile` is a `(hover: hover)` media
 * query, so a narrowed DESKTOP window is still "desktop" and the grid would
 * otherwise sit there paging one table at a time — the reported behaviour.
 */
export const gridIsTooNarrowToBeUseful = (tablesAcross: number): boolean => tablesAcross < 2;

/**
 * Where the grid sends the player when it stops being a grid — narrowed below
 * two cells, or the open-table count fell to one.
 *
 * The SELECTED table is preferred so the handoff feels continuous, but the
 * selection can be one render stale: exiting the selected table fires the
 * handoff effect before the keep-valid-selection effect has moved the
 * selection off the closed table. The old inline resolution trusted the stale
 * id, found no gameType for it, and fell through to the LOBBY — a player
 * closing one of two tables lost their remaining live table (EN-10538 AC1;
 * Cory's 2→1 report and Ryan's "exit takes me to lobby even though I am on
 * other tables", both 8/19). Which table the player happened to have focused
 * decided whether exit worked, which is why it looked intermittent.
 *
 * So: prefer the selection, but only ever hand off to a table that is LIVE in
 * state.tables right now, and answer "lobby" only when nothing is.
 */
/**
 * Whether the grid should paint NOTHING this frame. True on mobile (never
 * valid), with zero usable slots (transient reconnect / pre-redirect), or when
 * the WINDOW genuinely can't fit two cells — the narrow-desktop pager case,
 * where anything the grid painted would be a wrong page.
 *
 * REFINED for EN-10545 (8/20): the COUNT-driven handoff (window fits two,
 * tables fell to one) now PAINTS the lone cell instead of going dark. When
 * this guard shipped in EN-10538 the one-cell frame read as a wrong page —
 * grid chrome in a different color, no transition covering it. Both of those
 * are gone: the grid header now wears the room's background, and table↔grid
 * is an instant cut (the r10 motion contract; the 160ms crossfade it replaced
 * is long gone) — so the lone full-width cell is nearly identical to
 * the single view it fades into, and painting it is what makes 2→1 feel as
 * smooth as 4→3 (the leaving frame carries the same table the arriving view
 * shows). windowTablesThatFit must be the WINDOW's capacity (tablesThatFit),
 * NOT the count-clamped tablesAcross, or the count drop re-triggers the dark
 * frame this refinement removes.
 */
/**
 * How many tables the grid lays out: the loaded ones, plus each tournament
 * mid-rebalance (EN-10857). Its source is gone before its destination loads,
 * and counting only the loaded tables shrank the layout for that beat —
 * handing a two-table grid to single view and back, and reshaping the count
 * selector. `slotsInUse` and `listed` come from useActiveTables (the listed
 * tables plus their rebalance stand-ins, and the listed tables alone); the
 * rebalancing cell itself is kept by useGridSlots.
 */
export const gridLayoutCount = (loaded: number, slotsInUse: number, listed: number): number =>
  loaded + Math.max(0, slotsInUse - listed);

export const shouldSkipGridPaint = (
  isMobile: boolean,
  slotCount: number,
  windowTablesThatFit: number
): boolean => isMobile || slotCount === 0 || windowTablesThatFit < 2;

export const resolveGridHandoffTarget = (
  selectedGridGameID: string | number | null | undefined,
  orderedGameIDs: Array<string | number>,
  tables: Record<string, { game?: { game?: string } }> | undefined
): { gameID: string; gameType: string } | null => {
  const candidates =
    selectedGridGameID !== null && selectedGridGameID !== undefined
      ? [selectedGridGameID, ...orderedGameIDs]
      : orderedGameIDs;
  for (const id of candidates) {
    const gameType = tables?.[String(id)]?.game?.game;
    if (gameType) return { gameID: String(id), gameType: String(gameType) };
  }
  return null;
};

/**
 * Space between the header band and the top of the felts. Matches the 8px the
 * action rail already insets itself by, so the rail's own gap and the row's
 * gap read as one consistent margin rather than two arbitrary ones.
 */
export const GRID_HEADER_GAP_PX = 8;

/**
 * How long the row must be still before the snap nudges it to a whole-cell
 * alignment. Jason's number (2026-08-17): long enough that it can never
 * interrupt a pause mid-scroll, short enough that a half-cut table doesn't
 * linger. A constant so tuning it is one edit.
 */
export const GRID_SNAP_IDLE_MS = 2000;

/**
 * How long the grid must be genuinely empty before it gives up and returns to
 * the lobby. Long enough to outlast a socket reconnect's re-subscribe (the
 * tables vanish and come back within a few hundred ms), short enough that a
 * truly empty grid does not sit there looking broken.
 */
export const EMPTY_GRID_GRACE_MS = 1500;

/**
 * How long an empty grid waits before giving up when the table set has NEVER
 * hydrated this session — a cold start.
 *
 * Strictly LONGER than the refresh-restore watcher's ceiling on purpose: that
 * watcher (ViewManagerContext) owns the cold-start decision for a refresh into
 * /grid, and it decides on EVIDENCE — a processed GOT_PLAYER_GAMES push — not
 * on a clock. This timer is only the terminal backstop for cold paths the
 * watcher deliberately skips (the post-login landing), so it must never win a
 * race against a watcher that is still legitimately waiting.
 */
export const COLD_START_GRID_GRACE_MS = RESTORE_MAX_WAIT_MS + 2000;

/**
 * How long to let an EMPTY grid stand before returning to the lobby.
 *
 * Two very different situations produce zero tables, and telling them apart is
 * the whole point: a set that emptied after we had one is usually a half-open
 * socket re-subscribing and resolves in well under a second, while a set that
 * has never hydrated this session is a cold start still working through
 * connect -> auth -> GOT_PLAYER_GAMES -> subscribe -> first broadcast.
 */
export const gridEmptyGraceMs = (hasHydratedTables: boolean): number =>
  hasHydratedTables ? EMPTY_GRID_GRACE_MS : COLD_START_GRID_GRACE_MS;

/**
 * When a HELD too-narrow handoff re-checks its hold. Just past the hold's own
 * ceiling: the pending was armed no later than the moment the effect held, so
 * one timer at ceiling-plus-margin is always enough for hasPendingGridOpen()
 * to read false when it fires. (If a newer open re-armed the pending in the
 * meantime, the fired re-check simply holds again and re-schedules.)
 */
export const PENDING_GRID_OPEN_RECHECK_MS = PENDING_GRID_OPEN_MAX_MS + 250;

/**
 * The too-narrow handoff's hold for an in-flight direct-grid open — WITH the
 * self-heal that makes the hold's ceiling actually reachable (dev review
 * #3760). The stuck case is a subscribe that was silently dropped: the table
 * never lands, so none of the handoff effect's deps ever change again, and
 * hasPendingGridOpen()'s expiry is a ref read that re-renders nothing. So
 * holding must schedule its own re-evaluation. Returns null when there is
 * nothing to hold for (the handoff may proceed), otherwise the
 * effect-cleanup that cancels the scheduled re-check (the normal case: the
 * table lands, slots change, the effect re-runs before the timer fires).
 * Exported so the self-heal is unit-testable without mounting the full
 * provider tree the component needs.
 */
export const holdForPendingGridOpen = (
  hasPendingGridOpen: () => boolean,
  scheduleRecheck: () => void
): (() => void) | null => {
  if (!hasPendingGridOpen()) return null;
  const timer = window.setTimeout(scheduleRecheck, PENDING_GRID_OPEN_RECHECK_MS);
  return () => window.clearTimeout(timer);
};

/**
 * Bring a cell fully into view by scrolling the row HORIZONTALLY ONLY.
 *
 * Replaces `cell.scrollIntoView({ inline: 'nearest', block: 'nearest' })`, which
 * is also allowed to scroll VERTICALLY — and this row can be scrolled
 * vertically, because cells are deliberately unclipped so a seat's turn glow
 * can bleed between them (see Table/index.tsx). Tall transient content — the
 * action row appearing, a win celebration — makes the row's scrollHeight
 * exceed its clientHeight, so a scrollIntoView (or a focused button, or any
 * other agent) could set scrollTop and shove every table upward, where it
 * stayed until something happened to reset it: Jason's 16px nudge on every
 * turn, ~260px on a win, and "it doesn't go back until I click" (2026-08-18).
 *
 * Same semantics as `inline: 'nearest'` — a cell already fully visible does not
 * move — but it can only ever touch scrollLeft.
 */
export const scrollCellIntoViewHorizontally = (
  row: HTMLElement,
  cell: HTMLElement,
  smooth: boolean
): void => {
  const left = cell.offsetLeft;
  const right = left + cell.offsetWidth;
  const viewLeft = row.scrollLeft;
  const viewRight = viewLeft + row.clientWidth;
  let target: number;
  if (left < viewLeft) target = left;
  else if (right > viewRight) target = right - row.clientWidth;
  else return; // already fully on screen — 'nearest' means leave it alone
  row.scrollTo({ left: Math.max(0, target), behavior: smooth ? 'smooth' : 'auto' });
};

/**
 * The grid's cell order: the tiles' order, kept only where a cell can exist.
 * Pure and exported so "tiles and cells always agree" is a tested property
 * rather than a coincidence of two data sources.
 */
export const orderGridGameIDs = (tileOrder: string[], hydrated: Set<string>): string[] =>
  tileOrder.filter((id) => hydrated.has(id));

const MultiTableGrid = () => {
  const tables = useAppSelector((state) => (state as any).tables) as Record<string, any>;
  const offerGameId = useAppSelector((state) => state.waitlist.pendingOffer?.gameId);
  const {
    returnToLobby,
    activeView,
    selectedGridGameID,
    selectGridTable,
    openTable,
    handOffGridToTable,
    reportGridTableCount,
    hasPendingGridOpen,
    gridRebind,
    clearGridRebind,
    tablesInView,
    wasPostLoginLanding,
    restoreInFlight
  } = useContext(ViewManagerContext);
  const isMobile = useIsMobile();

  // Seeded from ViewManagerProvider's context value, NOT by calling
  // consumePostLoginLanding() here directly — that one-shot flag clears
  // itself on first read, and this component is always mounted (as
  // SlideContainer's gridPanel) regardless of route, so it would win the read
  // race against ViewManagerContext's own refresh-guard effect every time,
  // starving it of the flag on every guest login-return to a TABLE page. See
  // ViewManagerContext.tsx's wasPostLoginLanding comment for the full story.
  const [inPostLoginGrace, setInPostLoginGrace] = useState(wasPostLoginLanding);
  // Have we EVER seen tables this session? Until we have, an empty grid means
  // "still restoring", not "the player closed everything". Render-time latch,
  // same pattern as the other bookkeeping refs here — it only ever goes false
  // to true, so there is nothing to tear down.
  const hasHydratedTablesRef = useRef(false);

  useEffect(() => {
    if (!inPostLoginGrace) return;
    const timer = setTimeout(() => setInPostLoginGrace(false), POST_LOGIN_GRACE_MS);
    return () => clearTimeout(timer);
  }, [inPostLoginGrace]);

  // Cells follow the header tiles' order (useActiveTables). Only loaded
  // tables get a cell.
  const { activeTables: orderedActiveTables, slotsInUse } = useActiveTables();
  const hydratedIds = new Set(
    Object.entries(tables || {})
      .filter(([, table]) => table?.game?.gameID && table.game.gameID !== 0)
      .map(([gameID]) => gameID)
  );
  const openGameIDs = orderGridGameIDs(
    orderedActiveTables.map((t) => String(t.gameID)),
    hydratedIds
  );

  // How many tables to lay out. Width sets the CEILING (the shared fit math in
  // utils/tableViewWidth, so the grid, the header's strip mode and the narrow
  // handoff can never disagree about the same window) and the player's own
  // choice sits underneath it — a wide monitor may fit four felts, but someone
  // who asked for two gets two. clampTablesInView reconciles the two, and
  // re-reconciles on every resize, so dragging a window narrow demotes the
  // layout gracefully instead of overflowing.
  const viewportWidth = useViewportWidth();

  // When a balancing rebind has been applied (the destination table landed and
  // took over the source's cell), tear the source table down and clear the
  // signal in one step. Doing the removeTable HERE (rather than on a 3s timer)
  // is what stops the old table re-appearing as a fresh cell in the window
  // between the rebind resolving and the timer firing.
  const dispatch = useAppDispatch();
  const handleRebindConsumed = useCallback(() => {
    if (gridRebind?.from) {
      // Nothing to relink in the SHARED order (EN-10545/EN-10857): a
      // tournament table is tracked by tournamentId, not gameID, so the
      // moved-to table already sits in the moved-from table's slot — see
      // orderKeyOf in useActiveTables.
      dispatch(removeTable(gridRebind.from));
      // The cell can swap and the rebind clear in the same pass now that the
      // source stays on screen until the destination loads (useGridSlots), so
      // the selection-follow effect below may never see the rebind. Hand the
      // selection over here, or it would snap to the first cell.
      if (String(selectedGridGameID) === gridRebind.from) selectGridTable(gridRebind.to);
    }
    clearGridRebind();
  }, [gridRebind, dispatch, clearGridRebind, selectedGridGameID, selectGridTable]);

  // Map the open tables onto STABLE cell slots. A slot keeps its identity (and
  // React key) when an MTT balancing move re-points it to a new table, so the
  // cell rebinds in place instead of the old cell unmounting and a new one
  // mounting elsewhere (the close-and-reopen). `slots` also keeps a
  // being-balanced cell alive across the brief gap before its new table lands.
  const rawSlots = useGridSlots(openGameIDs, gridRebind, handleRebindConsumed);
  // useGridSlots appends new tables at the end; put the slots back in table
  // order. Same objects, so React keys are stable (a reorder, not a remount).
  // A rebind destination that hasn't loaded yet keeps its cell's position.
  const previousSlotIdsRef = useRef<number[]>([]);
  const slots = orderGridSlots(rawSlots, openGameIDs, previousSlotIdsRef.current);
  previousSlotIdsRef.current = slots.map((s) => s.slotId);
  const orderedGameIDs = slots.map((s) => s.gameID);

  // Count CELLS, not raw state.tables entries. The two differ for exactly one
  // window: an MTT balancing rebind. useGridSlots re-points the source cell
  // at the destination the instant the move is detected and keeps that cell
  // alive until the destination's first broadcast lands, but the SOURCE
  // slice leaves state.tables on the balancing handler's 3s timer — usually
  // before the destination arrives. Counting state.tables there read ONE
  // table at two cells, `gridIsTooNarrowToBeUseful(1)` fired the narrow
  // handoff below, and a two-table grid collapsed into single view of the
  // OTHER table mid-balancing — never the destination (EN-10625, QA Loom
  // 9:44→9:50). The lobby check above already reasons in cells
  // (`slots.length`); the layout and the handoff now agree with it.
  //
  // EN-10857's gridLayoutCount covers the other half of the same gap: once the
  // source is gone and no rebind holds a cell (it was consumed, retired, or
  // never armed in this grid), the tournament's slot protection still counts
  // the table in flight.
  //
  // The two signals run on clocks of their own — the rebind retires after
  // GRID_REBIND_MAX_MS, the slot protection after TOURNAMENT_ORDER_PROTECT_MS
  // and is renewed after the move's 3 s wait — so taking the larger of them
  // could hold a grid around a destination that never lands for ~23 s. They
  // are one hold: it starts with whichever appears first and lapses
  // GRID_REBIND_MAX_MS later, after which the grid counts the cells it shows.
  const layoutCount = gridLayoutCount(openGameIDs.length, slotsInUse, orderedActiveTables.length);
  const rebalanceInFlight = gridRebind !== null || layoutCount > openGameIDs.length;
  const rebalanceHoldLive = useBoundedHold(rebalanceInFlight, GRID_REBIND_MAX_MS);
  const gridTableCount = gridCountWithRebalanceHold(slots.length, layoutCount, rebalanceHoldLive);
  const tablesAcross = clampTablesInView(tablesInView, viewportWidth, gridTableCount);
  // The view manager's return to the grid uses this same count (EN-11092): with
  // its own count of state.tables, a loaded table this grid hides read as "two
  // tables, go back to the grid" right after the handoff below read "one table,
  // leave it", and the view flipped every broadcast. This grid stays mounted
  // behind single view, so the count stays current on /table too.
  useEffect(() => {
    reportGridTableCount(gridTableCount);
  }, [gridTableCount, reportGridTableCount]);

  // CAROUSEL (Jason, 2026-08-15) — every open table is rendered into ONE
  // horizontally scrollable row; nothing is sliced away onto a second page.
  // `tablesAcross` no longer decides what EXISTS, only how wide each table is:
  // each cell takes an equal share of the viewport minus the gaps, so exactly
  // that many fill the screen and the rest are a scroll away. Widening the
  // window grows the tables; narrowing shrinks them to the 390px floor and then
  // simply reveals fewer at a time. Replaces the 1/N pager, which is what made
  // players report "I lost my 4th table".
  const visibleSlots = slots;
  const cellBasis = cellFlexBasis(tablesAcross);

  // Re-run tick for the held handoff below. holdForPendingGridOpen bumps it
  // once the hold's ceiling has passed — the only way the effect re-runs in
  // the stuck case, where no other dep will ever change again.
  const [pendingRecheckTick, bumpPendingRecheck] = useReducer((n: number) => n + 1, 0);

  // Redirect off /grid back to the lobby when the grid shouldn't be shown:
  //   • Mobile (native app / mobile web) — the grid is desktop-only, so a
  //     stale or hand-typed /grid URL must not strand a mobile client here.
  //     The UI entry points are already hidden on mobile (see TableHeader);
  //     this is the route-level guard backing that up.
  //   • The user closed their last table — don't leave them on an empty grid.
  // Gated on activeView so the always-mounted hidden grid panel doesn't
  // trigger a navigate when the user is elsewhere.
  //
  // The empty-table check is suppressed during inPostLoginGrace: state.tables
  // is always empty on a fresh page load and only repopulates once the socket
  // resubscribes, so without this a guest who was in Multi-Table View when
  // they logged in would get bounced to the lobby before their tables ever
  // reappear. Mobile still redirects immediately regardless — the grid is
  // never valid there, post-login or not.
  useEffect(() => {
    if (activeView !== 'grid') return;
    if (shouldReturnToLobbyFromGrid(isMobile, slots.length, inPostLoginGrace)) {
      // Mobile is never valid here, so leave immediately. An EMPTY table set is
      // a different thing: a half-open socket forcing a reconnect empties
      // state.tables for a moment and then re-subscribes every table, and
      // bouncing on that transient threw the player out of the grid and back —
      // visible as the header collapsing 97px → 58px → 97px in Jason's capture
      // (2026-08-18). Real emptiness persists; a re-subscribe does not, so wait
      // long enough to tell them apart.
      if (isMobile) {
        returnToLobby();
        return undefined;
      }
      // COLD START vs REAL EMPTINESS. 1.5s is the right window for a table
      // set that emptied after we had one (a half-open socket re-subscribing).
      // It is far too short for a set that has never hydrated at ALL this
      // session: a hard refresh into /grid has to connect the socket,
      // authenticate, receive GOT_PLAYER_GAMES, subscribe per table and wait
      // for the first broadcast before state.tables has anything in it, and on
      // a lower environment that comfortably outruns 1.5s — so the player was
      // dropped in the lobby every time they refreshed the grid (Jason
      // 2026-08-24). A post-login landing is the SAME cold restore and was
      // already given 8s; a refresh just never qualified for it, because the
      // flag it keyed off is about logging in rather than about hydration.
      const bounce = setTimeout(() => {
        if (slotsRef.current === 0) returnToLobby();
      }, gridEmptyGraceMs(hasHydratedTablesRef.current));
      return () => clearTimeout(bounce);
    }
    // Narrowed past two-tables-wide (or down to one open table): hand off to
    // the REAL single-table view rather than sitting in grid chrome paging one
    // table at a time. `forceSingle` stops the sticky-grid preference bouncing
    // them straight back to /grid. Target resolution lives in
    // resolveGridHandoffTarget — see its note for why it must check the LIVE
    // table set instead of trusting selectedGridGameID (EN-10538 AC1).
    // POST-LOGIN GRACE — the same window the guard above just protected.
    // `tablesAcross` clamps to 1 while `state.tables` is still empty, so
    // `gridIsTooNarrowToBeUseful(1)` is true and this branch fired
    // `returnToLobby()` anyway: a guest in Multi-Table View who logged in was
    // bounced out of the grid by the branch underneath the guard that exists
    // to stop exactly that (JRam review, #3720). Nothing here can be judged
    // until the tables are live, so wait (#3721 gives the structurally
    // identical restore case its own `restoreInFlight` return, below).
    if (inPostLoginGrace) return undefined;
    if (gridIsTooNarrowToBeUseful(tablesAcross)) {
      // Mid-restore, a "too narrow" reading is a LIE: tables come back one
      // broadcast at a time, so the set transiently looks like a single open
      // table and this branch would hand the player to single-table view
      // before tables 2..n land — exactly the "refresh at 3-up comes back in
      // single view" repro (owner, 8/25). Hold while the watcher is still
      // hydrating; once it settles, a genuinely-single table (or a genuinely
      // narrow window) hands off as designed.
      if (restoreInFlight) return undefined;
      // Same lie, third teller (after the post-login grace above and the
      // restore hold): a DIRECT-GRID OPEN is mid-flight. openTable subscribed
      // a brand-new table and navigated here to wait for its first broadcast;
      // until it lands the grid reads one cell and this branch handed the view
      // to the OLD table — at which point the pending focus effect refuses to
      // yank the player off a different table's URL, so the new table joined
      // the strip while the view stayed stolen (EN-10588, "opening a second
      // table redirects back to the existing MTT"). The effect re-runs when
      // the table lands (slots.length changes) — and the hold is BOUNDED
      // (dev review #3760): a dropped subscribe means the table never lands
      // and no dep here ever changes again, so holding schedules its own
      // re-check for just past hasPendingGridOpen's expiry ceiling. On that
      // re-run the expired hold reads false and the handoff below (or its
      // lobby fallback) proceeds instead of stranding the player in one-cell
      // grid chrome.
      const holdCleanup = holdForPendingGridOpen(hasPendingGridOpen, bumpPendingRecheck);
      if (holdCleanup) return holdCleanup;
      const handoff = resolveGridHandoffTarget(selectedGridGameID, orderedGameIDs, tables);
      if (handoff) {
        // Through the handoff, so the grid comes back when a second table does.
        // Only the view-switch debounce rejects it (the table is already open,
        // so the cap and maintenance let it through): retry once that lapses,
        // since nothing else may re-run this.
        if (!handOffGridToTable(handoff.gameID, handoff.gameType)) {
          const retry = setTimeout(bumpPendingRecheck, TABLE_TRANSITION_MS + 50);
          return () => clearTimeout(retry);
        }
      } else {
        returnToLobby();
      }
    }
    // orderedGameIDs/tables are read only to resolve the handoff target; the
    // id-set is already captured by slots.length + selectedGridGameID.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeView,
    isMobile,
    slots.length,
    inPostLoginGrace,
    restoreInFlight,
    hasPendingGridOpen,
    pendingRecheckTick,
    returnToLobby,
    tablesAcross,
    selectedGridGameID,
    openTable
  ]);

  // Keep a valid selected table. If the selection closes it falls back to the
  // first cell; but if the selection is being REBOUND by a balancing move, it
  // FOLLOWS the table to its new gameID instead of snapping to the first cell
  // (the reported bug: focus jumped to the first table on balancing). See
  // resolveGridSelection.
  const activeIdsKey = orderedGameIDs.join(',');
  useEffect(() => {
    const target = resolveGridSelection(orderedGameIDs, selectedGridGameID, gridRebind);
    if (target !== null && target !== selectedGridGameID) {
      selectGridTable(target);
    }
    // activeIdsKey captures the id-set; selectGridTable is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdsKey, selectedGridGameID, gridRebind]);

  // Follow the player's table selection into view. Selecting a table — e.g.
  // tapping its fan in the top nav — only sets selectedGridGameID; if that
  // table is scrolled off the side of the carousel, bring it back (the
  // reported bug in the paged version: selecting a tab on page 2 while viewing
  // page 1 left the player stranded). `inline: 'nearest'` scrolls the minimum
  // distance, so a table already on screen doesn't move at all — the row never
  // jumps under a player who is mid-decision. Replaces the setGridPage follow.
  const rowRef = useRef<HTMLDivElement>(null);

  // Mouse drag-to-scroll for the row — extracted to its own hook (and tests)
  // per the PR #3721 review; see useMouseDragScroll for the full rationale
  // (selection/DnD guards, smooth-scroll suspension, trailing-click swallow).
  // Whether this render paints the row at all — hoisted above the hook because
  // the drag effect's only inputs are the ref (a stable object) and this flag.
  // When the grid skipped its paint while already on /grid, the effect ran once
  // against a null `rowRef.current`, bailed, and then NEITHER input ever
  // changed again — so the row could mount a frame later with no drag listener
  // and mouse drag-to-scroll stayed dead for the rest of the session. Folding
  // the skip into `enabled` makes the flag flip when the row actually appears,
  // which re-runs the effect and attaches to the real element.
  const skipGridPaint = shouldSkipGridPaint(isMobile, slots.length, tablesThatFit(viewportWidth));

  useMouseDragScroll(rowRef, activeView === 'grid' && !skipGridPaint);
  // Which cells are NEW this render — they get the slide-in-from-the-left
  // entrance (EN-10545: a table added to the carousel enters at position 0
  // from the left, so the motion should say so). Seeded with the CURRENT ids
  // on mount so entering the grid view doesn't stage a mass entrance; only a
  // table added while the grid is already the player's context animates.
  // Render-time ref bookkeeping, same pattern as the view-transition hold.
  const knownIdsRef = useRef<Set<string> | null>(null);
  const enteringIds = new Set<string>();
  {
    const current = new Set(orderedGameIDs.map(String));
    if (knownIdsRef.current === null) {
      knownIdsRef.current = current;
    } else {
      const known = knownIdsRef.current;
      current.forEach((id) => {
        if (!known.has(id)) enteringIds.add(id);
      });
      knownIdsRef.current = current;
    }
  }
  // Read by the empty-grid timer above so it judges the count at FIRE time
  // rather than the one captured when it armed.
  const slotsRef = useRef(0);
  slotsRef.current = slots.length;
  if (slots.length > 0) hasHydratedTablesRef.current = true;
  useEffect(() => {
    if (selectedGridGameID === null || selectedGridGameID === undefined) return;
    const row = rowRef.current;
    const cell = row?.querySelector<HTMLElement>(
      `[data-grid-cell="${String(selectedGridGameID)}"]`
    );
    if (row && cell) scrollCellIntoViewHorizontally(row, cell, !prefersReducedMotion());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGridGameID, activeIdsKey, tablesAcross]);

  // --- Live visibility + idle snap ------------------------------------------

  // Publish each cell's visible fraction to the header's fan-tile dots on every
  // scroll frame (rAF-throttled: scroll events can outrun frames, and the dots
  // cannot be seen to update faster than the screen paints anyway). Plus the
  // gentle snap: SNAP_IDLE_MS after the last scroll activity, ease the row to
  // the nearest whole-cell alignment so a table is never left cut in half
  // (Jason 2026-08-17). Deliberately NOT CSS scroll-snap, which grabs the row
  // mid-gesture and kills the free-momentum feel — this is a nudge that waits
  // until the player has plainly finished.
  useEffect(() => {
    const row = rowRef.current;
    if (!row || activeView !== 'grid') return undefined;

    let frame = 0;
    let snapTimer: ReturnType<typeof setTimeout> | null = null;

    const publish = () => {
      frame = 0;
      const view = row.getBoundingClientRect();
      const fractions: Record<string, number> = {};
      row.querySelectorAll<HTMLElement>('[data-grid-cell]').forEach((cell) => {
        const r = cell.getBoundingClientRect();
        fractions[String(cell.dataset.gridCell)] = visibleFraction(
          r.left,
          r.right,
          view.left,
          view.right
        );
      });
      publishGridVisibility(fractions);
    };

    const snap = () => {
      snapTimer = null;
      const cells = row.querySelectorAll<HTMLElement>('[data-grid-cell]');
      if (cells.length < 2) return;
      // Pitch from the first two cells — includes the flex gap without
      // hard-coding it here.
      const pitch = cells[1].offsetLeft - cells[0].offsetLeft;
      if (pitch <= 0) return;
      const target = Math.round(row.scrollLeft / pitch) * pitch;
      const max = row.scrollWidth - row.clientWidth;
      const clamped = Math.min(Math.max(0, target), max);
      if (Math.abs(clamped - row.scrollLeft) < 2) return;
      row.scrollTo({ left: clamped, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    };

    /**
     * This row scrolls horizontally and MUST NEVER hold a vertical offset.
     *
     * It is a scroll container, and its cells are deliberately unclipped so a
     * seat's turn glow can bleed between them, so tall transient content — the
     * action row, a win celebration — makes scrollHeight exceed clientHeight.
     * The browser then scrolls this row on its own to reveal whatever takes
     * FOCUS inside that overflowing region: measured in Chrome, a plain
     * `button.focus()` moved scrollTop by the full overflow with no scroll call
     * from us anywhere, and `overflow-y: hidden` does not prevent it (nor does
     * `clip`, which the spec downgrades to `hidden` when the other axis
     * scrolls). Every table shifted upward by exactly the overflow and stayed
     * there until something happened to reset scrollTop.
     *
     * That was Jason's bug (2026-08-18): ~16px when the action row appeared,
     * ~260px on a winning hand, and it "doesn't go back until I click" —
     * clicking selected a table, and the follow-selection effect reset it.
     */
    const pinToTop = () => {
      if (row.scrollTop !== 0) row.scrollTop = 0;
    };

    const onScroll = () => {
      pinToTop();
      if (!frame) frame = requestAnimationFrame(publish);
      if (snapTimer) clearTimeout(snapTimer);
      snapTimer = setTimeout(snap, GRID_SNAP_IDLE_MS);
    };

    publish();
    // And again next frame. Switching the count re-flows the cells, and their
    // felts re-scale through their own ResizeObservers a frame later; measuring
    // only synchronously here can catch the layout mid-settle.
    const settle = requestAnimationFrame(publish);

    row.addEventListener('scroll', onScroll, { passive: true });
    // focusin fires synchronously inside focus(), so this corrects the offset
    // within the same task — before the browser can paint the shifted frame.
    // The scroll listener alone would snap it back a frame later, which reads
    // as a flinch. Both are kept: focus is the cause we measured, not the only
    // thing that can scroll a scroll container.
    row.addEventListener('focusin', pinToTop);
    const resize =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => onScroll());
    // OBSERVE THE CELLS, not just the row. Changing the table count changes
    // cellBasis — the CELLS resize while the row keeps its width — and a
    // relayout fires no scroll event. With only the row observed, nothing
    // re-measured: going 4→3 left the fourth table's dot lit at full opacity
    // even though its cell had been pushed off screen (Jason 2026-08-17). It
    // self-corrected on the next scroll, which is exactly why it read as
    // intermittent. Watching the cells makes any width change a re-measure.
    resize?.observe(row);
    row.querySelectorAll<HTMLElement>('[data-grid-cell]').forEach((cell) => resize?.observe(cell));

    return () => {
      row.removeEventListener('scroll', onScroll);
      row.removeEventListener('focusin', pinToTop);
      resize?.disconnect();
      cancelAnimationFrame(settle);
      if (frame) cancelAnimationFrame(frame);
      if (snapTimer) clearTimeout(snapTimer);
      clearGridVisibility();
    };
    // tablesAcross re-runs this when the COUNT changes (new cell widths, and a
    // new set of nodes to observe); activeIdsKey when the set of cells changes.
  }, [activeView, activeIdsKey, tablesAcross]);

  // --- Follow the action -----------------------------------------------------

  // When a table needs the PLAYER (their turn, clock running) and it is not
  // fully on screen, bring it on. Several needing them at once must not
  // ping-pong the row: the FIRST to ask holds the view until resolved — acted
  // or timed out — then the next in line gets its scroll (Jason 2026-08-17,
  // agreed with Ryan and Victor). advanceActionQueue owns that ordering.
  // A pending multi-run vote joins the same queue: it is the other thing a
  // table can need the player for, and it needs them harder (EN-10237).
  const actingIdsKey = orderedActiveTables
    .filter((t) => t.isMyTurn || t.voteClock)
    .map((t) => String(t.gameID))
    .join(',');
  const votingIdsKey = orderedActiveTables
    .filter((t) => t.voteClock)
    .map((t) => String(t.gameID))
    .join(',');
  const actionQueueRef = useRef<string[]>([]);
  // The head we last SCROLLED to, not the queue's first entry — a vote can jump
  // the queue below, so "did the head change" has to be asked of the resolved
  // head or the next tick re-scrolls to a table the player has already seen.
  const actionHeadRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (activeView !== 'grid') return;
    const nowActing = actingIdsKey ? actingIdsKey.split(',') : [];
    actionQueueRef.current = advanceActionQueue(actionQueueRef.current, nowActing);
    // A VOTE outranks a turn outright — the window is seconds long, the chips
    // are already committed, and it cannot be retried. Between two votes the
    // queue's own FIFO order still decides, so simultaneous votes don't
    // ping-pong the row any more than simultaneous turns do.
    const voting = new Set(votingIdsKey ? votingIdsKey.split(',') : []);
    const previousHead = actionHeadRef.current;
    const head = actionQueueHead(actionQueueRef.current, voting);
    actionHeadRef.current = head;
    if (!head || head === previousHead) return;
    const row = rowRef.current;
    const cell = row?.querySelector<HTMLElement>(`[data-grid-cell="${head}"]`);
    if (!row || !cell) return;
    // Already fully visible → the player can see it; moving the row under them
    // would be the interruption this feature exists to avoid.
    const view = row.getBoundingClientRect();
    const r = cell.getBoundingClientRect();
    if (visibleFraction(r.left, r.right, view.left, view.right) >= 0.98) return;
    scrollCellIntoViewHorizontally(row, cell, !prefersReducedMotion());
  }, [actingIdsKey, votingIdsKey, activeView]);

  // A seat offer is a 30s urgent prompt rendered inline in the offered table's
  // cell — select that table so the row scrolls it into view (the effect above
  // follows the selection). Every open table has a cell on the one row, but a
  // cell scrolled out of the viewport would put the prompt where the player
  // cannot see it.
  useEffect(() => {
    if (activeView !== 'grid' || !offerGameId) return;
    if (orderedGameIDs.includes(String(offerGameId))) {
      selectGridTable(String(offerGameId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerGameId, activeView, activeIdsKey]);

  if (skipGridPaint) {
    // Mobile never renders the grid; slots===0 is the fallback for the brief
    // render before the redirect effect fires.
    //
    // The third gate is the WINDOW's capacity — tablesThatFit(viewportWidth),
    // not the count-clamped tablesAcross. It catches the narrow-desktop case:
    // a window too small for two cells would paint a one-cell "grid", a
    // full-width table wearing grid chrome, for the beat before the handoff
    // effect above leaves the grid. On a machine dropping frames that is
    // clearly visible mid-transition (Cory's "shows the 1 table for a frame",
    // 8/19-20, invisible on fast local builds). Painting nothing for that beat
    // reads as part of the transition; painting a wrong-width grid reads as a
    // glitch.
    //
    // The COUNT-driven handoff deliberately does NOT come through here any
    // more: when the window fits two and the tables fall to one, the lone cell
    // PAINTS, because that frame is what makes 2->1 feel like 4->3. See
    // shouldSkipGridPaint's docblock for why that changed in EN-10545.
    return null;
  }

  return (
    <div style={containerStyle}>
      {/* Entrance for a newly added cell only — a nudge from the left with a
          fade, enough to say "entered from the left" without shoving a full
          table across the screen (EN-10545). */}
      <style>{`@keyframes gridCellEnter{from{transform:translateX(-32px);opacity:0;}to{transform:translateX(0);opacity:1;}}`}</style>
      {/* panelView pin: during the table↔grid cut this panel stays painted
          after `activeView` flips, and an unpinned header morphed into the
          legacy Lobby + gameId-chip row for those frames — the isolated
          "old style header" flash (Jason 8/21). This header is the GRID's,
          whatever the global view says. */}
      <TableHeader compact panelView='grid' />
      {/* One maintenance marquee for the whole grid (each cell's TopAction
          suppresses its own so it isn't repeated per table). */}
      <MaintenanceMarquee />
      {/* Row of fixed-width phone-sized tables */}
      <div ref={rowRef} className='multi-table-grid-scroll' style={gridStyle}>
        {visibleSlots.map(({ slotId, gameID }) => {
          // No per-cell selected / turn styling: the felt carries both signals
          // itself (see cellStyle). Selection still drives behaviour — sound
          // routing, the narrow-viewport handoff target — just not a border.
          return (
            // Outer box keyed by the STABLE slotId so the cell persists (no
            // reflow / remove+add) when a balancing move re-points it to a new
            // table. The inner subtree is keyed by gameID so its per-table state
            // (GameProvider/GlobalProvider) resets on the swap — the same
            // "persistent surface" the single-table view uses.
            <div
              key={slotId}
              data-grid-cell={String(gameID)}
              style={{
                ...cellStyle,
                flexBasis: cellBasis,
                animation:
                  enteringIds.has(String(gameID)) && !prefersReducedMotion()
                    ? 'gridCellEnter 320ms cubic-bezier(0.2, 0.9, 0.24, 1) both'
                    : undefined
              }}
              onClick={() => selectGridTable(String(gameID))}>
              <React.Fragment key={gameID}>
                <GlobalProvider>
                  <GridModeProvider value={true}>
                    <OrientationOverrideProvider orientation='portrait'>
                      <TableIdProvider gameID={gameID}>
                        {/* SOUND: only the FOCUSED cell is an active table.
                            Without this the context defaults to true and every
                            cell plays its own bets, chips, deals and wins at
                            once — and because this grid stays MOUNTED behind
                            single-table view, that bleed is audible even when
                            the player is looking at one table (EN-10577).
                            `activeView === 'grid'` is what silences the whole
                            grid while it is the hidden panel; it also stops the
                            focused table double-playing from its cell and its
                            carousel panel at the same time. Attention cues
                            (your turn, countdown) opt out of this gate — see
                            hooks/useSound's `crossTable`. Interaction
                            consumers (modal/panel open flags, the MTT
                            auto-fold warning) opt out too via gridMode — see
                            hooks/useTableScopedOpen. */}
                        {/* EN-10249 AC2: shutdown state is scoped to this table ID.
                            The shared grid header sits outside the per-table provider,
                            so the notice must remain inside each cell. */}
                        <GridTableShutdownBanner />
                        {/* VISIBILITY, as opposed to the focus gate below.
                            This grid stays MOUNTED behind single-table view, so
                            every cell is `gridMode` with nobody looking at it.
                            Anything that starts a clock or escalates on a
                            table's behalf reads this instead — see
                            contexts/TableOnScreenContext. Every cell of a grid
                            that IS on screen is visible, focused or not. */}
                        <TableOnScreenProvider value={activeView === 'grid'}>
                          <TableActiveProvider
                            value={
                              activeView === 'grid' && String(gameID) === String(selectedGridGameID)
                            }>
                            <GameProvider handHistoryTableId={String(gameID)}>
                              <ScaleContextProvider>
                                <TableHistoryProvider>
                                  <TexasChatContextProvider>
                                    <GameInner gridMode />
                                  </TexasChatContextProvider>
                                </TableHistoryProvider>
                              </ScaleContextProvider>
                            </GameProvider>
                          </TableActiveProvider>
                        </TableOnScreenProvider>
                      </TableIdProvider>
                    </OrientationOverrideProvider>
                  </GridModeProvider>
                </GlobalProvider>
              </React.Fragment>
            </div>
          );
        })}
      </div>

      {/* No pager. Tables that don't fit are a horizontal scroll away — see
          gridStyle / the carousel note above. */}
    </div>
  );
};

export default MultiTableGrid;

const containerStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: 'linear-gradient(180deg, #1c1a14 0%, #12100c 100%)'
};

/**
 * The carousel row. Every open table lives here; the ones that don't fit are
 * reached by scrolling sideways (trackpad, shift-wheel, or by selecting the
 * table's fan in the nav strip, which scrolls it into view).
 *
 * `gap` is the space BETWEEN tables and belongs to the row, not to the tables —
 * no cell carries padding or margin of its own, so each felt still owns its
 * full share of the width and the gap can be tuned here alone (design call,
 * Jason 2026-08-15: 16px, enough air that a seat's turn glow falls off into
 * empty room rather than colliding with the next table).
 *
 * `justifyContent: flex-start` (not center): once the tables overflow, centring
 * would push the first one off the left edge, out of reach of the scroller.
 */
export const gridStyle: CSSProperties = {
  flex: 1,
  // A flex item's automatic minimum is its content, which would let a tall cell
  // push the row past its share and shove the whole column out of the viewport.
  // `overflowY: hidden` below already zeroes that, but saying it outright means
  // the invariant survives someone relaxing the overflow later.
  minHeight: 0,
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'stretch',
  gap: `${TABLE_GAP}px`,
  // Breathing room under the header band. Each cell's topmost content — the
  // table-id line, and the Sit Out / Exit Table rail 8px inside the felt — used
  // to start flush against the band's bottom edge, so the id line read as
  // clipped and the rail crowded the chrome above it (Jason 2026-08-17). The
  // gap is the row's to give: the cells are interchangeable and shouldn't each
  // carry an offset that only exists because of what sits above the row.
  paddingTop: GRID_HEADER_GAP_PX,
  // EN-10800: keep this a horizontal scroll container at all times so
  // transient descendant overflow cannot toggle its layout behavior. The
  // scrollbar chrome is hidden cross-browser in MultiTableGrid.scss; trackpad,
  // shift-wheel, drag, and programmatic scrolling continue to work.
  overflowX: 'scroll',
  overflowY: 'hidden',
  scrollbarWidth: 'none',
  scrollBehavior: 'smooth'
};

// `flex: 1 1 0` + `minWidth: 0` — every cell takes an EQUAL share of the row
// (see TABLE_MIN_WIDTH), so N tables each get 1/N of the viewport rather than a
// fixed 490px box with dead space beside it. minWidth stays 0 deliberately: the
// share can't drop below TABLE_MIN_WIDTH because tablesAcross is derived from
// it, and a real min-width would overflow the row on a viewport narrower than
// one table (mobile portrait), which gridStyle's overflow:hidden would then clip
// off the portrait control side-stacks anchored to left:0 / right:0 of the cell.
//
// Do NOT add `overflow: hidden` here. This tile is the fixed-size flex item that
// wraps GameInner; clipping it was the real cause of the EN grid "everything
// jumps up on the player's turn" bug (tournament AND cash) — with the tile
// clipped, the taller on-turn action content forced the flex:1 felt to shrink,
// its ResizeObserver re-scaled to the smaller size, and the percentage-anchored
// on-felt details + seats + action bar jumped up. The row wrapper (`gridStyle`)
// still has overflow:hidden, so tiles can't spill outside the grid; we just lose
// the per-tile rounded-corner clip (cosmetic).
/**
 * The cell is a POSITIONING BOX ONLY — no border, no radius, no glow (design
 * call, Jason 2026-08-15).
 *
 * The grid should read like standing in a card room looking at four real
 * tables, not like four panels in a dashboard. Every pixel of chrome between
 * the felts works against that, so the cell draws nothing: the felt ovals sit
 * directly on the room's dark background and the space between them is the
 * only separation there is. Matches the mobile multi-table surface, where
 * swiping between tables shows no divider either.
 *
 * TURN AWARENESS DOES NOT LIVE HERE. It is carried three times over, all of it
 * ON the felt where the player is already looking:
 *   - the nav-strip card fan for that table shows ACT,
 *   - the POV seat plays its own turn glow (useSeatAnimation),
 *   - the seat's countdown ring runs.
 * A fourth signal drawn around the whole tile was redundant and, at four
 * tables, read as UI rather than as urgency. Removing it also drops the
 * per-cell selected/turn border styles entirely — nothing else consumed them.
 */
/**
 * Sizing: the cell NEITHER grows NOR shrinks — `flexBasis` (set at the call
 * site from `cellBasis`) is the whole story. Under the old pager the cell was
 * `flex: 1 1 0`, i.e. "share out whatever the row has", which is right when
 * the row can only ever hold one page. In a carousel it is wrong: five tables
 * in a 1440px row would each shrink to 275px and silently break the 390px
 * floor rather than overflowing into the scroll. `flex-shrink: 0` is what
 * makes the extra tables scroll instead of squeeze.
 */
export const cellStyle: CSSProperties = {
  position: 'relative',
  flexGrow: 0,
  flexShrink: 0,
  minWidth: 0,
  cursor: 'pointer'
};

export const __TABLE_MIN_WIDTH = TABLE_MIN_WIDTH;
export const __TABLE_GAP = TABLE_GAP;
